/**
 * KpaOverview - Tableau de bord principal de KPA Ton Concours
 * Métriques : utilisateurs, crédits, recherches, chatbot, revenus IAP
 */
import {
  AlertCircle,
  BarChart3,
  Bell,
  Coins,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface KpaStats {
  totalUsers: number;
  activeUsersLast7Days: number;
  newUsersToday: number;
  totalSearches: number;
  searchesToday: number;
  chatbotInteractions: number;
  totalCreditsGranted: number;
  totalCreditsPurchased: number;
  totalRevenue: number;
  revenueCurrency: string;
  totalConcours: number;
  publishedConcours: number;
  pendingNotifications: number;
  totalReferrals: number;
  validatedReferrals: number;
}

interface KpaOverviewProps {
  onNavigate: (section: string) => void;
}

const KpaOverview: React.FC<KpaOverviewProps> = ({ onNavigate }) => {
  const [stats, setStats] = useState<KpaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [concoursRes, statsRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/pka/concours?limit=1`),
        authFetch(`${API_BASE_URL}/api/pka/stats`),
      ]);

      const concoursData = await concoursRes.json();
      let kpaStats: Partial<KpaStats> = {
        totalConcours: concoursData.pagination?.total || 0,
        publishedConcours: concoursData.pagination?.total || 0,
      };

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        kpaStats = { ...kpaStats, ...statsData };
      }

      setStats({
        totalUsers: kpaStats.totalUsers ?? 0,
        activeUsersLast7Days: kpaStats.activeUsersLast7Days ?? 0,
        newUsersToday: kpaStats.newUsersToday ?? 0,
        totalSearches: kpaStats.totalSearches ?? 0,
        searchesToday: kpaStats.searchesToday ?? 0,
        chatbotInteractions: kpaStats.chatbotInteractions ?? 0,
        totalCreditsGranted: kpaStats.totalCreditsGranted ?? 0,
        totalCreditsPurchased: kpaStats.totalCreditsPurchased ?? 0,
        totalRevenue: kpaStats.totalRevenue ?? 0,
        revenueCurrency: kpaStats.revenueCurrency ?? 'XOF',
        totalConcours: kpaStats.totalConcours ?? 0,
        publishedConcours: kpaStats.publishedConcours ?? 0,
        pendingNotifications: kpaStats.pendingNotifications ?? 0,
        totalReferrals: kpaStats.totalReferrals ?? 0,
        validatedReferrals: kpaStats.validatedReferrals ?? 0,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <span className="text-lg">🔍</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">KPA Ton Concours</h1>
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-semibold">V1 PROD</span>
          </div>
          <p className="text-gray-500 text-sm">Organisation & Information — Trouver et suivre les concours</p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* KPIs principaux */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="w-5 h-5 text-orange-500" />}
          label="Utilisateurs total"
          value={stats?.totalUsers ?? 0}
          sub={`+${stats?.newUsersToday ?? 0} aujourd'hui`}
          color="orange"
        />
        <StatCard
          icon={<Search className="w-5 h-5 text-blue-500" />}
          label="Recherches total"
          value={stats?.totalSearches ?? 0}
          sub={`${stats?.searchesToday ?? 0} aujourd'hui`}
          color="blue"
        />
        <StatCard
          icon={<MessageCircle className="w-5 h-5 text-purple-500" />}
          label="Interactions IA"
          value={stats?.chatbotInteractions ?? 0}
          sub="Chatbot + Coach IA"
          color="purple"
        />
        <StatCard
          icon={<Coins className="w-5 h-5 text-yellow-500" />}
          label="Revenus IAP"
          value={stats?.totalRevenue ?? 0}
          sub={stats?.revenueCurrency ?? 'XOF'}
          color="yellow"
          isAmount
        />
      </div>

      {/* Métriques secondaires */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-green-500" />}
          label="Actifs 7 jours"
          value={stats?.activeUsersLast7Days ?? 0}
          sub="utilisateurs actifs"
          color="green"
        />
        <StatCard
          icon={<BarChart3 className="w-5 h-5 text-indigo-500" />}
          label="Concours publiés"
          value={stats?.publishedConcours ?? 0}
          sub={`${stats?.totalConcours ?? 0} total`}
          color="indigo"
        />
        <StatCard
          icon={<Bell className="w-5 h-5 text-red-500" />}
          label="Notifs programmées"
          value={stats?.pendingNotifications ?? 0}
          sub="en attente d'envoi"
          color="red"
        />
        <StatCard
          icon={<Zap className="w-5 h-5 text-teal-500" />}
          label="Parrainages validés"
          value={stats?.validatedReferrals ?? 0}
          sub={`${stats?.totalReferrals ?? 0} total`}
          color="teal"
        />
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickActionCard
          emoji="🔍"
          title="Gérer les Concours"
          description="Ajouter, modifier et publier des concours"
          color="from-orange-500 to-amber-500"
          onClick={() => onNavigate('kpa-concours')}
          badge="PROD"
        />
        <QuickActionCard
          emoji="👥"
          title="Utilisateurs KPA"
          description="Crédits, recherches, profils AI Coach"
          color="from-blue-500 to-indigo-500"
          onClick={() => onNavigate('kpa-users')}
        />
        <QuickActionCard
          emoji="🔔"
          title="Notifications"
          description="Rappels, deadlines, nouveaux concours"
          color="from-purple-500 to-pink-500"
          onClick={() => onNavigate('kpa-notifications')}
        />
        <QuickActionCard
          emoji="💰"
          title="Crédits & Achats"
          description="Transactions IAP, packs crédits"
          color="from-yellow-500 to-orange-500"
          onClick={() => onNavigate('kpa-credits')}
        />
        <QuickActionCard
          emoji="⚡"
          title="Parrainage"
          description="Codes, récompenses, filleuls"
          color="from-teal-500 to-cyan-500"
          onClick={() => onNavigate('kpa-referral')}
        />
        <QuickActionCard
          emoji="📊"
          title="Statistiques KPA"
          description="Recherches, catégories, rétention"
          color="from-green-500 to-emerald-500"
          onClick={() => onNavigate('kpa-analytics')}
        />
      </div>

      {/* Distinction produits */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
        <h3 className="font-semibold text-orange-800 mb-2">🔍 Rôle de KPA Ton Concours</h3>
        <div className="grid md:grid-cols-2 gap-3 text-sm text-orange-700">
          <div>
            <p className="font-medium mb-1">Ce que KPA fait :</p>
            <ul className="space-y-1">
              <li>• Trouver et centraliser les concours CI</li>
              <li>• Checklist documents personnalisée</li>
              <li>• Rappels automatiques (deadlines)</li>
              <li>• Assistant IA (questions administratives)</li>
              <li>• Recommandations selon le profil (AI Coach)</li>
            </ul>
          </div>
          <div>
            <p className="font-medium mb-1">Ce que KPA ne fait PAS :</p>
            <ul className="space-y-1">
              <li>• Quiz et révisions → <strong>Boss Ton Concours</strong></li>
              <li>• Cours et leçons → <strong>Boss Ton Concours</strong></li>
              <li>• Entraînement épreuves → <strong>Boss Ton Concours</strong></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Components ──────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub?: string;
  color: string;
  isAmount?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sub, isAmount }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
    <div className="flex items-center justify-between mb-3">
      <span className="text-gray-500 text-xs">{label}</span>
      {icon}
    </div>
    <p className="text-2xl font-bold text-gray-900">
      {isAmount
        ? value.toLocaleString('fr-FR')
        : value.toLocaleString('fr-FR')}
    </p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

interface QuickActionCardProps {
  emoji: string;
  title: string;
  description: string;
  color: string;
  onClick: () => void;
  badge?: string;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({ emoji, title, description, color, onClick, badge }) => (
  <div
    onClick={onClick}
    className={`bg-gradient-to-br ${color} rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition transform hover:-translate-y-0.5`}
  >
    <div className="flex items-start justify-between mb-3">
      <span className="text-3xl">{emoji}</span>
      {badge && (
        <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full font-semibold">{badge}</span>
      )}
    </div>
    <h3 className="font-semibold text-base mb-1">{title}</h3>
    <p className="text-sm text-white/80">{description}</p>
  </div>
);

export default KpaOverview;
